import { isUploadedDocument } from '@app/types/UploadADoc';
import React from 'react';
import { ApiModel, FormModel } from '../../ExchangeOfContracts.model';
import { getUserInfo } from '@app/selectors/user';
import { useSelector } from 'react-redux';
import { ExchangeOfContracts, AutoPositioningAnnotationsStatus } from '@app/types/ExchangeOfContracts';
import useToast from '@app/hooks/useToast';
import { ToastTypes } from '@app/common/components/Toast';

const AUTO_POSITION_FAILED_MESSAGE =
    'Our system has automatically placed the details on the contract, and we’ve detected a few fields that may need adjusting. Please ensure everything looks right and adjust the placeholders before sending.';
type Params = {
    eocDocument: ExchangeOfContracts;
    localDocumentList: ApiModel['uploadedDocuments'];
    save: (values: FormModel) => Promise<void>;
    setSelectedUploadedDocId: (id: string | undefined) => void;
    salesContractId?: string;
};

export const usePdfViewer = ({
    eocDocument,
    localDocumentList,
    save,
    setSelectedUploadedDocId,
    salesContractId
}: Params) => {
    const loggedInUser = useSelector(getUserInfo);
    const { addNewToast } = useToast();

    const [isPdfViewerOpen, setIsPdfViewerOpen] = React.useState(false);
    const [isSavingForPdfViewer, setIsSavingForPdfViewer] = React.useState(false);
    // Track if we have shown the autopositioning failure toast for this contract
    // to avoid showing it multiple times if the user opens the PDF viewer again,
    // but still show it again if the user uploads a new sales id
    const [shownToastForContractId, setShownToastForContractId] = React.useState<string>();

    const openPdfViewer = async (values: FormModel) => {
        const firstUploadedDocument = localDocumentList.find(document => isUploadedDocument(document));
        if (firstUploadedDocument) {
            setIsSavingForPdfViewer(true);

            await save(values);

            setIsSavingForPdfViewer(false);
            setSelectedUploadedDocId(firstUploadedDocument.id);
            setIsPdfViewerOpen(true);

            // Show autopositioning failure toast if status is failed and we haven't shown it for this contract
            if (
                eocDocument.autoPositionAnnotationsStatus === AutoPositioningAnnotationsStatus.FAILED &&
                salesContractId &&
                shownToastForContractId !== salesContractId
            ) {
                addNewToast(AUTO_POSITION_FAILED_MESSAGE, ToastTypes.WARNING, true);
                setShownToastForContractId(salesContractId);
            }
        }
    };

    const closePdfViewer = React.useCallback(() => {
        setIsPdfViewerOpen(false);
        setSelectedUploadedDocId(undefined);
    }, [setIsPdfViewerOpen, setSelectedUploadedDocId]);

    const memoizedAgencyDetails = React.useMemo(() => {
        return {
            name: loggedInUser?.agency?.details.agencyName,
            imageUrl: loggedInUser?.agency?.logo
        };
    }, [loggedInUser]);

    const memoizedAgent = React.useMemo(() => {
        return {
            fullName: eocDocument.agent.fullName,
            accountType: eocDocument.agent.accountType
        };
    }, [eocDocument]);

    return {
        memoizedAgencyDetails,
        memoizedAgent,
        closePdfViewer,
        openPdfViewer,
        isPdfViewerOpen,
        isSavingForPdfViewer
    };
};
